/**
 * wf-shared — shared UI + calendar-math helpers for the Workforce screens.
 * All visual values are jrni tokens (T / alpha); no hardcoded hex.
 * Wall-clock convention: workforce payloads carry `date` (YYYY-MM-DD) +
 * "HH:MM" times in the LOCATION's tz (see wf-api.ts / spec §4), so grid math
 * here is pure string/minute arithmetic — no Date-zone conversion.
 */
import { useMemo, useState, type CSSProperties, type ReactNode } from "react";
import { Btn, Icon, Input, Pill, T, alpha } from "@v0y4ge/jrni-ui";
import { FilterDropdown } from "../../shell/composites";
import { useLocations } from "../locations/useLocations";
import type { DemandSlice, DemandSource, TimeOffRequest, WfShift } from "./wf-api";
import { useT, t as i18nT } from "../../lib/i18n";

/* ── Demand-source vocabulary (one hue per source, everywhere) ── */ // ui-config
export const SOURCE_META: Record<DemandSource, { label: string; color: string }> = {
  events: { label: "Events", color: T.tPurple },
  appointments: { label: "Appointments", color: T.tDarkBlue },
  queue: { label: "Walk-ins & queues", color: T.tOrange },
};
export const SOURCE_IDS: DemandSource[] = ["events", "appointments", "queue"];

export function SourceDot({ kind, size = 8 }: { kind: DemandSource; size?: number }) {
  return <span aria-hidden style={{ width: size, height: size, borderRadius: "50%", background: SOURCE_META[kind].color, display: "inline-block", flexShrink: 0 }} />;
}

export function SourceLegend({ style }: { style?: CSSProperties }) {
  return (
    <div style={{ display: "flex", gap: 14, alignItems: "center", ...style }}>
      {SOURCE_IDS.map((s) => (
        <span key={s} style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, color: T.soft }}>
          <SourceDot kind={s} /> {SOURCE_META[s].label}
        </span>
      ))}
    </div>
  );
}

/* ── Wall-clock helpers ─────────────────────────────────────────── */
export const pad2 = (n: number) => String(n).padStart(2, "0");
export const hmToMin = (hm: string): number => {
  const [h, m] = hm.split(":").map((x) => parseInt(x, 10));
  return (h || 0) * 60 + (m || 0);
};
export const minToHm = (min: number): string => `${pad2(Math.floor(min / 60))}:${pad2(min % 60)}`;
/** "13:30" → "1:30 PM" (deliberately simple 12h; locale-config formatting is for real datetimes). */
export function hmLabel(hm: string): string {
  const m = hmToMin(hm);
  const h = Math.floor(m / 60), mi = m % 60;
  const ap = h >= 12 ? "PM" : "AM";
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return mi === 0 ? `${h12} ${ap}` : `${h12}:${pad2(mi)} ${ap}`;
}
export function hmRangeLabel(a: string, b: string): string { return `${hmLabel(a)} — ${hmLabel(b)}`; }

/* ── Calendar-date (YYYY-MM-DD) helpers — UTC-pinned so labels never
 *    shift a day across timezones ── */
export function isoToday(): string {
  const d = new Date();
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}
export function mondayOf(iso?: string): string {
  const base = iso ?? isoToday();
  const [y, m, d] = base.split("-").map((x) => parseInt(x, 10));
  const dt = new Date(Date.UTC(y, m - 1, d));
  const dow = dt.getUTCDay(); // 0 Sun … 6 Sat
  dt.setUTCDate(dt.getUTCDate() - ((dow + 6) % 7));
  return dt.toISOString().slice(0, 10);
}
export function addDaysIso(iso: string, days: number): string {
  const [y, m, d] = iso.split("-").map((x) => parseInt(x, 10));
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() + days);
  return dt.toISOString().slice(0, 10);
}
export function weekDates(weekStart: string): string[] {
  return Array.from({ length: 7 }, (_, i) => addDaysIso(weekStart, i));
}
const _dayFmt = (opts: Intl.DateTimeFormatOptions) => new Intl.DateTimeFormat(undefined, { ...opts, timeZone: "UTC" });
export function dayLabel(iso: string, style: "wk" | "wk-d" | "md" | "full" = "wk-d"): string {
  const [y, m, d] = iso.split("-").map((x) => parseInt(x, 10));
  const dt = new Date(Date.UTC(y, m - 1, d));
  if (style === "wk") return _dayFmt({ weekday: "short" }).format(dt);
  if (style === "wk-d") return _dayFmt({ weekday: "short", day: "numeric" }).format(dt);
  if (style === "md") return _dayFmt({ month: "short", day: "numeric" }).format(dt);
  return _dayFmt({ weekday: "short", month: "short", day: "numeric" }).format(dt);
}
export function weekRangeLabel(weekStart: string): string {
  return `${dayLabel(weekStart, "md")} – ${dayLabel(addDaysIso(weekStart, 6), "md")}`;
}

/** Half-hour option list for time pickers, e.g. ["7:00 AM", "7:30 AM", …]. */
export function halfHourOptions(startHour = 6, endHour = 23): string[] {
  const out: string[] = [];
  for (let h = startHour; h <= endHour; h++) { out.push(hmLabel(`${pad2(h)}:00`)); if (h < endHour) out.push(hmLabel(`${pad2(h)}:30`)); }
  return out;
}
export function labelToHm(label: string): string {
  const m = label.match(/^(\d{1,2})(?::(\d{2}))?\s*(AM|PM)$/i);
  if (!m) return "09:00";
  let h = parseInt(m[1], 10) % 12;
  if (/pm/i.test(m[3])) h += 12;
  return `${pad2(h)}:${m[2] || "00"}`;
}

/* ── Week navigation (‹ Today ›) ────────────────────────────────── */
export function WeekNav({ weekStart, onChange, testid = "wf-weeknav" }: { weekStart: string; onChange: (ws: string) => void; testid?: string }) {
  const t = useT();
  const navBtn: CSSProperties = { width: 30, height: 30, borderRadius: 6, border: `1px solid ${T.border}`, background: T.white, color: T.n2, cursor: "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center", boxShadow: T.shadowChrome };
  return (
    <div style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
      <button data-testid={`${testid}-prev`} aria-label={t("workforce.wfShared.previousWeek", "Previous week")} style={navBtn} onClick={() => onChange(addDaysIso(weekStart, -7))}><Icon.chev dir="left" size={13} /></button>
      <button data-testid={`${testid}-today`} style={{ ...navBtn, width: "auto", padding: "0 12px", fontSize: 12.5, fontWeight: 600, fontFamily: T.font }} onClick={() => onChange(mondayOf())}>{t("workforce.wfShared.today", "Today")}</button>
      <button data-testid={`${testid}-next`} aria-label={t("workforce.wfShared.nextWeek", "Next week")} style={navBtn} onClick={() => onChange(addDaysIso(weekStart, 7))}><Icon.chev dir="right" size={13} /></button>
      <span style={{ fontSize: 13, fontWeight: 600, color: T.n1, marginLeft: 2, whiteSpace: "nowrap" }}>{weekRangeLabel(weekStart)}</span>
    </div>
  );
}

/* ── Location filter (existing listLocations op) ────────────────── */
export function LocationFilter({ value, onChange }: { value: string | null; onChange: (id: string | null) => void }) {
  const t = useT();
  const { locations } = useLocations();
  const current = locations.find((l) => l.id === value);
  return (
    <FilterDropdown
      label={current ? current.name : t("reports.overview.allLocations", "All locations")}
      active={!!value}
      options={locations.map((l) => l.name)}
      onPick={(name) => { const hit = locations.find((l) => l.name === name); onChange(hit ? hit.id : null); }}
      onClear={() => onChange(null)}
    />
  );
}

/* ── Demand stack cell (one 30-min slot) ────────────────────────── */
export function DemandStack({ slice, max, scheduled, width, height = 46 }: {
  slice: DemandSlice; max: number; scheduled?: number; width: number; height?: number;
}) {
  const total = slice.events + slice.appointments + slice.queue;
  const px = (n: number) => (max > 0 ? Math.round((n / max) * (height - 4)) : 0);
  const short = scheduled !== undefined && scheduled < total;
  return (
    <div style={{ width, height, position: "relative", display: "flex", flexDirection: "column", justifyContent: "flex-end", flexShrink: 0 }}
      title={i18nT("workforce.wfShared.sliceTNeedTotalScheduledUndefinedScheduledScheduledEventsSliceEventsAppt.icu", { time: slice.t, total, scheduleState: scheduled !== undefined ? "shown" : "hidden", scheduled, events: slice.events, appointments: slice.appointments, queue: slice.queue })}>
      {short ? <div style={{ position: "absolute", inset: 0, background: alpha(T.red1, 8) }} /> : null}
      <div style={{ width: Math.max(3, width - 3), margin: "0 auto", display: "flex", flexDirection: "column-reverse", borderRadius: 2, overflow: "hidden" }}>
        <span style={{ height: px(slice.appointments), background: SOURCE_META.appointments.color, display: "block" }} />
        <span style={{ height: px(slice.queue), background: SOURCE_META.queue.color, display: "block" }} />
        <span style={{ height: px(slice.events), background: SOURCE_META.events.color, display: "block" }} />
      </div>
      {scheduled !== undefined ? (
        <span style={{ position: "absolute", left: 1, right: 1, bottom: 2 + px(scheduled), height: 2, background: short ? T.red1 : T.n1, opacity: 0.75 }} />
      ) : null}
    </div>
  );
}

/* ── Status pills ───────────────────────────────────────────────── */
export function severityPill(severity: "critical" | "low"): ReactNode {
  return severity === "critical" ? <Pill tone="danger" dot>{i18nT("workforce.wfShared.critical", "Critical")}</Pill> : <Pill tone="warn" dot>{i18nT("workforce.wfShared.low", "Low")}</Pill>;
}
export function readinessPill(r: "ready" | "partial" | "unstaffed"): ReactNode {
  if (r === "ready") return <Pill tone="success" dot>{i18nT("workforce.wfShared.staffed", "Staffed")}</Pill>;
  if (r === "partial") return <Pill tone="warn" dot>{i18nT("workforce.wfShared.partial", "Partial")}</Pill>;
  return <Pill tone="danger" dot>{i18nT("workforce.wfShared.unstaffed", "Unstaffed")}</Pill>;
}
export function requestStatusPill(s: "pending" | "approved" | "declined"): ReactNode {
  if (s === "approved") return <Pill tone="success" dot>{i18nT("workforce.wfShared.approved", "Approved")}</Pill>;
  if (s === "declined") return <Pill tone="danger" dot>{i18nT("workforce.wfShared.declined", "Declined")}</Pill>;
  return <Pill tone="warn" dot>{i18nT("workforce.wfShared.pending", "Pending")}</Pill>;
}

/** Stable hue per workforce role label (presentation only). */ // ui-config
const ROLE_HUE_POOL = [T.tPurple, T.tDarkBlue, T.tTurquoise, T.tOrange, T.tPink, T.tGreen, T.tLightBlue, T.tRed];
export function roleHue(role: string): string {
  const n = (role || "").split("").reduce((a, c) => a + c.charCodeAt(0), 0);
  return ROLE_HUE_POOL[n % ROLE_HUE_POOL.length];
}

/* ── Rule-flag chip (labor-rule warning on a shift/request) ─────── */
export function RuleFlags({ flags }: { flags: string[] }) {
  if (!flags || flags.length === 0) return null;
  return (
    <span style={{ display: "inline-flex", gap: 4, flexWrap: "wrap" }}>
      {flags.map((f) => (
        <span key={f} title={i18nT("workforce.wfShared.laborRuleF.icu", { value0: f })} style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 10.5, fontWeight: 600, color: T.orange1, background: alpha(T.orange1, 12), borderRadius: 999, padding: "1px 7px" }}>
          <Icon.warn size={10} /> {f}
        </span>
      ))}
    </span>
  );
}

/* ── Not-yet-available state (ops declared, not yet served) ──
   Customer-facing (CC 2026-10-19 walk 3 item 3): no operation name, spec path or data-layer wording on screen.
   The operation stays in data-op for support and tests; a raw error is never printed. */
export function WfPending({ opName, onRetry, screen, title, body }: { opName: string; error?: string | null; onRetry?: () => void; screen: string; title?: string; body?: string }) {
  const t = useT();
  return (
    <div data-testid={`${screen}-pending-state`} data-op={opName} role="status" style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", padding: 40 }}>
      <div style={{ maxWidth: 460, textAlign: "center", display: "flex", flexDirection: "column", alignItems: "center", gap: 12 }}>
        <span style={{ width: 52, height: 52, borderRadius: 12, background: T.p4, color: T.p1, display: "inline-flex", alignItems: "center", justifyContent: "center" }}><Icon.clock size={22} /></span>
        <h2 style={{ fontSize: 19, fontWeight: 700, color: T.n1, margin: 0 }}>{title ?? t("workforce.wfShared.notAvailableYet", "This information isn't available yet")}</h2>
        <p style={{ fontSize: 13.5, color: T.n2, lineHeight: 1.55, margin: 0 }}>{body ?? t("workforce.wfShared.notAvailableYetBody", "It will appear here as soon as your workspace provides it.")}</p>
        {onRetry ? (
          <Btn testid={`${screen}-pending-retry`} size="sm" variant="secondary" onClick={onRetry}>{t("common.actions.retry", "Retry")}</Btn>
        ) : null}
      </div>
    </div>
  );
}

/* ── Shared self-service modals (Swap / Time off) — used by MySchedule and
 * the bank-persona My Time port; one definition avoids the jrni-ui-style
 * "fixed 3x" duplication problem. ── */
export const TIMEOFF_TYPES: { id: TimeOffRequest["type"]; label: string; labelKey: string }[] = [
  { id: "vacation", label: "Vacation", labelKey: "workforce.wfShared.vacation" },
  { id: "sick", label: "Sick", labelKey: "workforce.wfShared.sick" },
  { id: "personal", label: "Personal", labelKey: "workforce.wfShared.personal" },
  { id: "other", label: "Other", labelKey: "workforce.wfShared.other" },
];

export function ModalShell({ title, sub, onClose, children }: { title: string; sub?: string; onClose: () => void; children: ReactNode }) {
  const t = useT();
  return (
    <div role="dialog" aria-label={title} onClick={onClose} style={{ position: "fixed", inset: 0, zIndex: 240, background: alpha(T.n1, 45), display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
      <div onClick={(e) => e.stopPropagation()} style={{ width: 440, maxWidth: "100%", background: T.white, borderRadius: 12, boxShadow: T.shadowLg, overflow: "hidden", fontFamily: T.font }}>
        <div style={{ padding: "16px 20px 12px", borderBottom: `1px solid ${T.border}`, display: "flex", alignItems: "flex-start", gap: 10 }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: T.n1 }}>{title}</div>
            {sub ? <div style={{ fontSize: 12, color: T.soft, marginTop: 2 }}>{sub}</div> : null}
          </div>
          <button aria-label={t("common.actions.close", "Close")} onClick={onClose} style={{ width: 30, height: 30, borderRadius: 8, border: "none", background: "transparent", color: T.n2, cursor: "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center" }}><Icon.x size={14} /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

export function SwapModal({ shift, busy, onClose, onSubmit }: { shift: Pick<WfShift, "id" | "role" | "date" | "start" | "end">; busy: string | null; onClose: () => void; onSubmit: (reason: string) => Promise<void> }) {
  const t = useT();
  const [reason, setReason] = useState(""); // ui-local (buffer; persisted on submit)
  return (
    <ModalShell title={t("workforce.wfShared.requestASwap", "Request a swap")} sub={/* TODO(i18n): replace template-literal with t('workforce.wfShared.sub', { ... }) */`${shift.role ? shift.role + " · " : ""}${dayLabel(shift.date, "full")} · ${hmRangeLabel(shift.start, shift.end)}`} onClose={onClose}>
      <div style={{ padding: 20, display: "flex", flexDirection: "column", gap: 14 }}>
        <div>
          <span style={{ fontSize: 12, fontWeight: 600, color: T.n1, display: "block", marginBottom: 6 }}>{t("workforce.wfShared.reasonOptional", "Reason (optional)")}</span>
          <Input value={reason} placeholder={t("workforce.wfShared.eGChildcareConflict", "e.g. childcare conflict")} onChange={(e) => setReason(e.target.value)} testid="wfmy-swap-reason-input" />
        </div>
        <span style={{ fontSize: 12, color: T.soft }}>{t("workforce.wfShared.theShiftGoesToYourTeamAsASwapOfferOnceA", "The shift goes to your team as a swap offer once a manager approves.")}</span>
      </div>
      <div style={{ padding: "12px 20px", borderTop: `1px solid ${T.border}`, display: "flex", justifyContent: "flex-end", gap: 8 }}>
        <Btn variant="secondary" onClick={onClose}>{t("common.actions.cancel", "Cancel")}</Btn>
        <Btn testid="wfmy-swap-submit" disabled={busy !== null} onClick={() => { void onSubmit(reason); }}>{busy === "swap" ? t("workforce.wfShared.submitting", "Submitting…") : t("workforce.wfShared.submitRequest", "Submit request")}</Btn>
      </div>
    </ModalShell>
  );
}

export function TimeOffModal({ busy, initialDate, onClose, onSubmit }: { busy: string | null; initialDate?: string; onClose: () => void; onSubmit: (i: { type: TimeOffRequest["type"]; startDate: string; endDate: string; allDay: boolean; note?: string }) => Promise<void> }) {
  const t = useT();
  const [type, setType] = useState<TimeOffRequest["type"]>("vacation"); // ui-local
  const [startDate, setStartDate] = useState(() => initialDate ?? addDaysIso(isoToday(), 1)); // ui-local
  const [endDate, setEndDate] = useState(() => initialDate ?? addDaysIso(isoToday(), 1)); // ui-local
  const [note, setNote] = useState(""); // ui-local
  const dateOptions = useMemo(() => Array.from({ length: 60 }, (_, i) => addDaysIso(isoToday(), i)), []);
  const lbl = (d: string) => dayLabel(d, "full");
  const valid = endDate >= startDate;
  return (
    <ModalShell title={t("workforce.wfShared.requestTimeOff", "Request time off")} onClose={onClose}>
      <div style={{ padding: 20, display: "flex", flexDirection: "column", gap: 14 }}>
        <div>
          <span style={{ fontSize: 12, fontWeight: 600, color: T.n1, display: "block", marginBottom: 6 }}>{t("workforce.wfShared.type", "Type")}</span>
          <FilterDropdown label={(() => { const item = TIMEOFF_TYPES.find((item) => item.id === type); return item ? t(item.labelKey, item.label) : "Type"; })()} active options={TIMEOFF_TYPES.map((item) => t(item.labelKey, item.label))}
            onPick={(l) => setType((TIMEOFF_TYPES.find((item) => t(item.labelKey, item.label) === l) ?? TIMEOFF_TYPES[0]).id)} onClear={() => setType("vacation")} />
        </div>
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
          <div style={{ flex: 1, minWidth: 150 }}>
            <span style={{ fontSize: 12, fontWeight: 600, color: T.n1, display: "block", marginBottom: 6 }}>{t("workforce.wfShared.firstDay", "First day")}</span>
            <FilterDropdown label={lbl(startDate)} active options={dateOptions.map(lbl)}
              onPick={(l) => { const d = dateOptions.find((x) => lbl(x) === l); if (d) { setStartDate(d); if (endDate < d) setEndDate(d); } }} onClear={() => undefined} />
          </div>
          <div style={{ flex: 1, minWidth: 150 }}>
            <span style={{ fontSize: 12, fontWeight: 600, color: T.n1, display: "block", marginBottom: 6 }}>{t("workforce.wfShared.lastDay", "Last day")}</span>
            <FilterDropdown label={lbl(endDate)} active options={dateOptions.filter((d) => d >= startDate).map(lbl)}
              onPick={(l) => { const d = dateOptions.find((x) => lbl(x) === l); if (d) setEndDate(d); }} onClear={() => undefined} />
          </div>
        </div>
        <div>
          <span style={{ fontSize: 12, fontWeight: 600, color: T.n1, display: "block", marginBottom: 6 }}>{t("workforce.wfShared.noteOptional", "Note (optional)")}</span>
          <Input value={note} placeholder={t("workforce.wfShared.anythingYourManagerShouldKnow", "Anything your manager should know")} onChange={(e) => setNote(e.target.value)} testid="wfmy-timeoff-note-input" />
        </div>
        {!valid ? <span style={{ fontSize: 12, color: T.red1 }}>{t("workforce.wfShared.theLastDayCanTBeBeforeTheFirstDay", "The last day can't be before the first day.")}</span> : null}
      </div>
      <div style={{ padding: "12px 20px", borderTop: `1px solid ${T.border}`, display: "flex", justifyContent: "flex-end", gap: 8 }}>
        <Btn variant="secondary" onClick={onClose}>{t("common.actions.cancel", "Cancel")}</Btn>
        <Btn testid="wfmy-timeoff-submit" disabled={busy !== null || !valid} onClick={() => { void onSubmit({ type, startDate, endDate, allDay: true, note: note || undefined }); }}>{busy === "timeoff" ? t("workforce.wfShared.submitting", "Submitting…") : t("workforce.wfShared.submitRequest", "Submit request")}</Btn>
      </div>
    </ModalShell>
  );
}

/* ── Tiny fraction progress (e.g. roles filled 3/4) ─────────────── */
export function TinyProgress({ num, den, width = 72 }: { num: number; den: number; width?: number }) {
  const pct = den > 0 ? Math.min(100, Math.round((num / den) * 100)) : 0;
  const tone = pct >= 100 ? T.green1 : pct > 0 ? T.orange1 : T.red1;
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
      <span style={{ width, height: 5, borderRadius: 999, background: T.n6, overflow: "hidden", display: "inline-block" }}>
        <span style={{ display: "block", width: `${pct}%`, height: "100%", background: tone }} />
      </span>
      <span style={{ fontSize: 12, color: T.n2, fontFamily: T.fontMono }}>{num}/{den}</span>
    </span>
  );
}

/* ── Pre/post appointment block (2026-08-03, operator ruling) ─────────────
 * An appointment consumes preMin + durationMin + postMin of an advisor's day. Everywhere an
 * appointment is drawn, the CORE is solid and the shoulders are the same hue at low opacity,
 * so "45 minutes" never reads as the whole cost of a 75-minute block. Pre/post come from the
 * SERVICE CATALOGUE (services.json → getMyServiceScope / getServiceCatalogue), joined by
 * service name — the appointment record itself carries no pre/post.
 */
export interface ApptBlockSpec { preMin: number; durationMin: number; postMin: number }
export function apptBlockTotal(b: ApptBlockSpec): number { return (b.preMin || 0) + (b.durationMin || 0) + (b.postMin || 0); }

/** Horizontal bar — list rows, catalogue rows, drawers. */
export function ApptBlockBar({ block, width = 104, height = 8, hue = T.p1, title }: {
  block: ApptBlockSpec; width?: number; height?: number; hue?: string; title?: string;
}) {
  const total = apptBlockTotal(block);
  if (!total) return null;
  const pct = (n: number) => `${Math.max(0, ((n || 0) / total) * 100)}%`;
  return (
    <span aria-hidden data-testid="appt-block-bar"
      title={title ?? `${block.preMin} + ${block.durationMin} + ${block.postMin} min`}
      style={{ display: "inline-flex", width, height, borderRadius: height / 2, overflow: "hidden", background: T.n6, flexShrink: 0 }}>
      {block.preMin ? <span style={{ width: pct(block.preMin), background: alpha(hue, 26) }} /> : null}
      <span style={{ width: pct(block.durationMin), background: hue }} />
      {block.postMin ? <span style={{ width: pct(block.postMin), background: alpha(hue, 26) }} /> : null}
    </span>
  );
}

/** Vertical band — day/week timelines, where height IS time. */
export function ApptBlockBand({ block, height, hue = T.p1, radius = 6 }: {
  block: ApptBlockSpec; height: number; hue?: string; radius?: number;
}) {
  const total = apptBlockTotal(block);
  if (!total || !height) return null;
  const h = (n: number) => Math.max(0, ((n || 0) / total) * height);
  return (
    <span aria-hidden style={{ display: "flex", flexDirection: "column", width: 5, height, borderRadius: radius, overflow: "hidden", background: T.n6, flexShrink: 0 }}>
      {block.preMin ? <span style={{ height: h(block.preMin), background: alpha(hue, 26) }} /> : null}
      <span style={{ height: h(block.durationMin), background: hue }} />
      {block.postMin ? <span style={{ height: h(block.postMin), background: alpha(hue, 26) }} /> : null}
    </span>
  );
}

/** Legend, so the lighter shoulders are explained once per surface rather than guessed. */
export function ApptBlockLegend({ hue = T.p1 }: { hue?: string }) {
  const t = i18nT;
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 14, fontSize: 11, color: T.soft }}>
      <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
        <span style={{ width: 14, height: 7, borderRadius: 2, background: hue }} />
        {t("workforce.wfShared.blockCore", "Appointment")}
      </span>
      <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
        <span style={{ width: 14, height: 7, borderRadius: 2, background: alpha(hue, 26) }} />
        {t("workforce.wfShared.blockShoulders", "Prep & wrap-up")}
      </span>
    </span>
  );
}
